import { Global } from '@emotion/react'
import React from 'react'

import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  Heading5,
  Icon,
  JustifyContent,
  Modal,
  Spinner,
  Text,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { DeviceType, useDeviceType } from '@library/dom'

import type { RecipeTileCustomisationData } from './RecipeTile.types'

type RecipeTileCustomisationDropdownProps = {
  customisationData?: RecipeTileCustomisationData
  onClose?: () => void
}

type CustomisationDropdownShellProps = {
  children: React.ReactNode
  onClose?: () => void
}

type CustomisationOptionPillProps = {
  text: string
  isSelected: boolean
  isUnavailable: boolean
}

const preventPropagation = (event: React.SyntheticEvent) => event.stopPropagation()
const CUSTOMISATION_MODAL_PORTAL_CLASS = 'recipe-tile-customisation-modal-portal'

const CustomisationCloseButton = withCitrusTheme('button', () => ({
  appearance: 'none',
  alignItems: AlignItems.Center,
  backgroundColor: 'transparent',
  border: 0,
  borderRadius: '999px',
  cursor: 'pointer',
  display: Display.Flex,
  height: '40px',
  justifyContent: JustifyContent.Center,
  margin: 0,
  padding: 0,
  width: '40px',
  '&:focus-visible': {
    outline: '2px solid #615CFF',
    outlineOffset: '2px',
  },
}))

const CustomisationOptionPill = ({
  text,
  isSelected,
  isUnavailable,
}: CustomisationOptionPillProps) => (
  <Box
    alignItems={AlignItems.Center}
    aria-current={isSelected ? 'true' : undefined}
    aria-disabled={isUnavailable ? 'true' : undefined}
    bg={isSelected ? Color.Secondary_50 : Color.NeutralGrey_50}
    borderColor={isSelected ? Color.Secondary_400 : Color.NeutralGrey_200}
    borderStyle={BorderStyle.Solid}
    borderWidth={isSelected ? 2 : 1}
    color={isUnavailable && !isSelected ? Color.ColdGrey_500 : Color.ColdGrey_800}
    display={Display.Flex}
    justifyContent={JustifyContent.Center}
    maxWidth="100%"
    minHeight="40px"
    minWidth="160px"
    paddingH={4}
    style={{
      borderRadius: '100px',
      textDecoration: isUnavailable && !isSelected ? 'line-through' : undefined,
    }}
  >
    <Text
      size={1}
      textAlign={TextAlign.Center}
      fontWeight={FontWeight.Bold}
      style={{ overflowWrap: 'anywhere' }}
    >
      {text}
    </Text>
  </Box>
)

const drawerModalStyle = {
  overlay: {
    alignItems: 'flex-end',
    backgroundColor: 'rgba(51, 61, 71, 0.48)',
    display: 'flex',
    justifyContent: 'center',
    zIndex: 1000,
  },
  content: {
    border: 'none',
    borderRadius: 0,
    bottom: 0,
    inset: 'auto 0 0 0',
    left: 0,
    padding: 0,
    position: 'absolute',
    right: 0,
    top: 'auto',
    width: '100%',
  },
} as const

const getModalAppElement = () => {
  if (typeof document === 'undefined') {
    return undefined
  }

  return document.getElementById('react-root') ?? undefined
}

const CustomisationDrawerHeader = ({
  onClose,
}: Pick<CustomisationDropdownShellProps, 'onClose'>) => (
  <Box
    alignItems={AlignItems.Center}
    display={Display.Flex}
    justifyContent={JustifyContent.FlexEnd}
    paddingBottom={2}
  >
    <CustomisationCloseButton
      type="button"
      aria-label="Close customisation"
      onClick={onClose}
      data-testid="recipe-tile-customisation-close"
    >
      <Icon name="close" />
    </CustomisationCloseButton>
  </Box>
)

const CustomisationDropdownShell = ({ children, onClose }: CustomisationDropdownShellProps) => {
  const deviceType = useDeviceType()
  const isDesktop = deviceType === DeviceType.DESKTOP
  const appElement = getModalAppElement()

  if (!isDesktop) {
    return (
      <>
        <Global
          styles={{
            [`.${CUSTOMISATION_MODAL_PORTAL_CLASS} .ReactModal__Content > div`]: {
              width: '100%',
            },
          }}
        />
        <Modal
          {...(appElement ? { appElement } : { ariaHideApp: false })}
          contentLabel="Customise recipe"
          isOpen
          name="recipe-tile-customisation-modal"
          onRequestClose={onClose}
          portalClassName={CUSTOMISATION_MODAL_PORTAL_CLASS}
          shouldCloseOnEsc
          shouldCloseOnOverlayClick
          style={drawerModalStyle}
        >
          <Box
            bg={Color.White}
            boxShadow="0 -8px 24px rgba(51, 61, 71, 0.24)"
            maxHeight="calc(100dvh - 64px)"
            onClick={preventPropagation}
            onKeyDown={preventPropagation}
            style={{
              borderRadius: '12px 12px 0 0',
              overflowY: 'auto',
              padding: '1rem',
            }}
            data-testid="recipe-tile-customisation-dropdown"
          >
            <CustomisationDrawerHeader onClose={onClose} />
            {children}
          </Box>
        </Modal>
      </>
    )
  }

  return (
    <Box
      bg={Color.White}
      boxShadow="0 18px 14px 0 rgba(51, 61, 71, 0.2)"
      left="0"
      onClick={preventPropagation}
      onKeyDown={preventPropagation}
      position={DisplayPosition.Absolute}
      right="0"
      top="100%"
      style={{
        padding: '1rem',
        zIndex: 40,
      }}
      data-testid="recipe-tile-customisation-dropdown"
    >
      {children}
    </Box>
  )
}

export const RecipeTileCustomisationDropdown = ({
  customisationData,
  onClose,
}: RecipeTileCustomisationDropdownProps) => {
  if (customisationData?.isLoadingCustomisations) {
    return (
      <CustomisationDropdownShell onClose={onClose}>
        <Box
          width="100%"
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
          paddingV={4}
        >
          <Spinner color={Color.ColdGrey_900} size={6} />
        </Box>
      </CustomisationDropdownShell>
    )
  }

  if (customisationData?.hasCustomisationError || !customisationData?.customisations) {
    return (
      <CustomisationDropdownShell onClose={onClose}>
        <Text size={2}>Customisation unavailable</Text>
      </CustomisationDropdownShell>
    )
  }

  return (
    <CustomisationDropdownShell onClose={onClose}>
      <Box
        data-testid="recipe-tile-customisation-options"
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={5}
      >
        {customisationData.customisations.slots.map((slot) => (
          <Box key={slot.text}>
            <Heading5>{slot.text}</Heading5>
            <Box display={Display.Flex} gap={3} wrap style={{ paddingTop: '8px' }}>
              {slot.values.map((value) => {
                const isSelected = value.recipeId === customisationData.customisationRecipe?.id
                const isUnavailable = !value.recipeId || !value.isAvailable

                return (
                  <Box key={`${slot.text}-${value.name}`} display={Display.Flex}>
                    <CustomisationOptionPill
                      text={value.name}
                      isSelected={isSelected}
                      isUnavailable={isUnavailable}
                    />
                  </Box>
                )
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </CustomisationDropdownShell>
  )
}
